import { isNumber } from '../guards/index.js';

const formatFileSize = (bytes, minimum = 4e3) => {
  const units = ["KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  let value = Math.max(bytes, minimum);
  let unitIndex = -1;
  while (value >= 1e3 && unitIndex < units.length - 1) {
    value /= 1e3;
    unitIndex++;
  }
  if (unitIndex === -1) {
    return `${Math.ceil(value / 1e3).toFixed(0)} KB`;
  }
  return `${value.toFixed(1)} ${units[unitIndex]}`.replace(".0", "");
};
const parseIntOrString = (value) => {
  if (typeof value === "number") return value;
  return parseInt(value, 10);
};
function formatCoordinates(location) {
  const {
    latitude,
    longitude
  } = location;
  let lat = Math.abs(latitude).toFixed(5);
  let lon = Math.abs(longitude).toFixed(5);
  if (latitude > 0) {
    lat += "° N";
  } else {
    lat += "° S";
  }
  if (longitude > 0) {
    lon += "° E";
  } else {
    lon += "° W";
  }
  return `${lat}, ${lon}`;
}
function formatMinutesSeconds(totalSeconds, padWithZeros = true) {
  if (totalSeconds < 0 || totalSeconds === Infinity || isNaN(totalSeconds)) {
    return padWithZeros ? "00:00" : "0:00";
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.min(Math.round(totalSeconds % 60), 59);
  const paddedSeconds = String(seconds).padStart(2, "0");
  const paddedMinutes = padWithZeros ? String(minutes).padStart(2, "0") : minutes;
  return `${paddedMinutes}:${paddedSeconds}`;
}
function formatHoursMinutesSeconds(totalSeconds, padWithZeros = true) {
  if (totalSeconds <= 0 || totalSeconds === Infinity || isNaN(totalSeconds)) {
    return padWithZeros ? "00:00:00" : "0:00:00";
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.min(Math.floor(totalSeconds % 3600 / 60), 59);
  const seconds = Math.min(Math.round(totalSeconds % 60), 59);
  const paddedHours = padWithZeros ? String(hours).padStart(2, "0") : hours;
  const paddedMinutes = String(minutes).padStart(2, "0");
  const paddedSeconds = String(seconds).padStart(2, "0");
  return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
}
function formatDuration(totalSeconds, padWithZeros = true) {
  if (totalSeconds <= 0 || totalSeconds === Infinity || isNaN(totalSeconds)) {
    return padWithZeros ? "00:00" : "0:00";
  }
  const hours = Math.floor(totalSeconds / 3600);
  if (hours > 0) {
    return formatHoursMinutesSeconds(totalSeconds, padWithZeros);
  } else {
    return formatMinutesSeconds(totalSeconds, padWithZeros);
  }
}
function formatBitrate(bitrate) {
  const units = ["bps", "Kbps", "Mbps", "Gbps"];
  let unitIndex = 0;
  while (bitrate >= 1e3 && unitIndex < units.length - 1) {
    bitrate /= 1e3;
    unitIndex++;
  }
  return `${bitrate.toFixed(2)} ${units[unitIndex]}`;
}
function formatSampleRate(sampleRate) {
  return `${(sampleRate / 1e3).toFixed(1)} kHz`;
}
const sum = (values) => values.reduce((acc, value) => acc + value, 0);
const romanDigits = {
  M: 1e3,
  CM: 900,
  D: 500,
  CD: 400,
  C: 100,
  XC: 90,
  L: 50,
  XL: 40,
  X: 10,
  IX: 9,
  V: 5,
  IV: 4,
  I: 1
};
function numberToRoman(number) {
  if (!isNumber(number)) {
    throw new Error("Number is not a number");
  }
  let roman = "";
  let num = number;
  for (const [i, romanDigit] of Object.entries(romanDigits)) {
    const q = Math.floor(num / romanDigit);
    num -= q * romanDigit;
    roman += i.repeat(q);
  }
  return roman;
}
const letterDigits = "abcdefghijklmnopqrstuvwxyz";
function numberToLetter(number) {
  if (!isNumber(number) || number < 0 || !Number.isInteger(number)) {
    throw new Error("Input must be a non-negative integer.");
  }
  const effectiveBase1Idx = number === 0 ? 1 : number;
  let letter = "";
  let current = effectiveBase1Idx;
  while (current > 0) {
    current--;
    letter = letterDigits[current % 26] + letter;
    current = Math.floor(current / 26);
  }
  return letter;
}
function formatCurrency(amount, currency, options) {
  return new Intl.NumberFormat(void 0, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options
  }).format(amount);
}
function formatPercentage(value, options) {
  return new Intl.NumberFormat(void 0, {
    style: "percent",
    ...options
  }).format(value);
}
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export { clamp, formatBitrate, formatCoordinates, formatCurrency, formatDuration, formatFileSize, formatHoursMinutesSeconds, formatMinutesSeconds, formatPercentage, formatSampleRate, numberToLetter, numberToRoman, parseIntOrString, sum };
//# sourceMappingURL=index.js.map
